import {PropsWithChildren, useEffect, useMemo} from "react";
import {appAuth, appFirestore,} from "../index";
import {useAuthState, useSignInWithEmailAndPassword} from "react-firebase-hooks/auth";
import LoginView, {LoginCredentials} from "../app/views/LoginView";
import {useCollection} from "react-firebase-hooks/firestore";
import {collection, DocumentData, getDocs, query, QuerySnapshot, where} from "firebase/firestore";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {setUsers, setUsersError, setUsersLoading} from "../features/AppAuthSlice";
import {Col, Container, Row, Spinner} from "react-bootstrap";
import {employeesDataConverter, FirebaseEmployeeLoader} from "./FirebaseEmployeeLoader";
import {AppUsersType, AppUserType} from "../app/types/AppUserType";
import {FirebaseEventsLoader} from "./FirebaseEventsLoader";
import {RREventStatusCurrent} from "../app/types/RREventStatusType";
import {FirebaseCalendarLoader} from "./FirebaseCalendarLoader";
import {
    selectCalendarFirstDate, selectCalendarVisibleDays,
    selectCurrentUser,
    setCurrentEmployee,
    setCurrentUser, setDepartment
} from "../features/AppStateSlice";
import {FirebaseBankHolidaysLoader} from "./FirebaseBankHolidaysLoader";
import {firebaseDateToDate} from "../app/tools";
import {selectAllEmployees} from "../features/AppDataSlice";
import {RREmployeeType} from "../app/types/RREmployeeType";
import {FirebaseEmployeeStatisticsLoader} from "./FirebaseEmployeeStatisticsLoader";
import {FirebaseWarehouseLocksLoader} from "./FirebaseWarehouseLockLoader";

export const FirebaseWrapper: React.FC<PropsWithChildren<{}>> = (props) => {
    const [currentUser, currentUserLoading] = useAuthState(appAuth);
    const employees = useAppSelector(selectAllEmployees);
    const activeUser = useAppSelector(selectCurrentUser);

    const [
        signInWithEmailAndPassword,
        user, authLoading, authError
    ] = useSignInWithEmailAndPassword(appAuth);

    const tryLogin = (credentials: LoginCredentials) => signInWithEmailAndPassword(credentials.email, credentials.password);

    const usersCollection = useMemo(() => collection(appFirestore, "users"), []);
    const [users, loading, error] = useCollection(usersCollection);
    const dispatch = useAppDispatch();
    const firstDate = useAppSelector(selectCalendarFirstDate);
    const visibleDays = useAppSelector(selectCalendarVisibleDays);

    useEffect(() => {
        dispatch(setUsers(convertFirebaseDataToUsers(users)));
        dispatch(setUsersLoading(loading));
        dispatch(setUsersError(error?.message));

        if (users) {
            const currentUser = users.docs.find(
                d => d.get("email").toLowerCase() === user?.user.email?.toLowerCase()
            );

            dispatch(setCurrentUser(convertFirebaseDataToUser(currentUser!)));
        }
    }, [users, loading, error]);

    useEffect(() => {
        if (users && currentUser) {
            const _user = users.docs.find(
                d => d.get("email").toLowerCase() === currentUser.email?.toLowerCase()
            );

            dispatch(setCurrentUser(convertFirebaseDataToUser(_user!)));
        } else {
            dispatch(setCurrentUser(undefined));
        }
    }, [currentUser, users]);

    async function findEmployeeByEmail(email: string) {
        try {
            const emplDoc = await getDocs(query(collection(appFirestore, "employees"),
                where("email", "==", email)));
            if(emplDoc.docs.length === 0) return undefined;
            return employeesDataConverter(emplDoc.docs)[0];
        } catch (e) {
            console.error(e);
            return undefined;
        } finally {

        }
    }

    useEffect(() => {
        if (currentUser && currentUser.email) {
            findEmployeeByEmail(currentUser.email.toLocaleLowerCase()).then(
                (empl) => {
                    dispatch(setCurrentEmployee(empl));
                }
            );
        }
        dispatch(setCurrentEmployee(undefined));
    }, [currentUser]);


    if (loading || authLoading || currentUserLoading) return <Container fluid>
        <Row className={"vh-100 align-content-center justify-content-center"}>
            <Col xs={"auto"}>
                <Spinner className={"big-spinner"}/>
            </Col>
        </Row>
    </Container>

    if (!user && !loading && !authLoading && !currentUser) return <LoginView onTryLogin={tryLogin} loading={authLoading}
                                                                             error={authError?.message}/>;

    return <>
        <FirebaseEmployeeLoader>
            <FirebaseEmployeeStatisticsLoader>
                <FirebaseEventsLoader withStatus={RREventStatusCurrent}>
                    <FirebaseCalendarLoader firstDate={firstDate} daysCount={visibleDays || 7}>
                        <FirebaseBankHolidaysLoader/>
                        <FirebaseWarehouseLocksLoader />
                    </FirebaseCalendarLoader>
                </FirebaseEventsLoader>
            </FirebaseEmployeeStatisticsLoader>
        </FirebaseEmployeeLoader>
        {props.children}
    </>;
}

export function convertFirebaseDataToUser(user: DocumentData): AppUserType {
    if (!user) return {
        validPeriod: {
            from: new Date(),
            to: new Date()
        },
        name: "",
        email: "",
        phone: "",
    }

    const value = user;
    return {
        id: value.id,
        ref: value.ref,
        name: value.get("name")!,
        employeeId: value.get("employeeId"),
        email: value.get("email")!,
        phone: value.get("phone") || "",
        validPeriod: {
            from: firebaseDateToDate(value.get("validFromDate")) || new Date(),
            to: firebaseDateToDate(value.get("validToDate"))
        }
    } as unknown as AppUserType;
}

export function convertFirebaseDataToUsers(users: QuerySnapshot<DocumentData> | undefined): AppUsersType {
    if (!users) return [];

    return users.docs.map(value => convertFirebaseDataToUser(value))
}

